import React from 'react';
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { DataSurface } from '@/components/platform/DataSurface';
import { TooltipLabel } from '@/components/ui/TooltipLabel';
import { Skeleton } from '@/components/ui/skeleton';

export interface DemoChartDataPoint {
  date: string;
  total: number;
  qualified: number;
  booked: number;
  notBooked: number;
}

export type TimePeriod = '7d' | '1m' | '3m' | '6m' | '1y';

const rangeTabs: TimePeriod[] = ['7d', '1m', '3m', '6m', '1y'];
const TOTAL_LINE_COLOR = 'hsl(210 85% 60%)';
const QUALIFIED_LINE_COLOR = 'hsl(151 58% 46%)';

interface SessionsChartProps {
  data: DemoChartDataPoint[];
  period: TimePeriod;
  onPeriodChange: (period: TimePeriod) => void;
  isLoading?: boolean;
  className?: string;
}

const chartConfig = {
  total: {
    label: 'All sessions',
    color: TOTAL_LINE_COLOR,
  },
  qualified: {
    label: 'Qualified',
    color: QUALIFIED_LINE_COLOR,
  },
} satisfies ChartConfig;

export const SessionsChart: React.FC<SessionsChartProps> = ({
  data,
  period,
  onPeriodChange,
  isLoading = false,
  className,
}) => {
  const header = (
    <div className="flex h-[50px] flex-row items-center justify-between border-b border-border/80 px-4">
      <TooltipLabel
        label="Timeline"
        description="Session volume and qualification over time."
        className="text-sm font-medium text-foreground"
      />
      <Tabs value={period} onValueChange={(v) => onPeriodChange(v as TimePeriod)}>
        <TabsList className="h-[30px] card-secondary-bg border border-default">
          {rangeTabs.map((p) => (
            <TabsTrigger
              key={p}
              value={p}
              className={cn(
                'text-xs px-2.5 h-6',
                'data-[state=active]:bg-card data-[state=active]:shadow-sm',
                'data-[state=active]:text-heading',
                'text-muted-foreground'
              )}
            >
              {p}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );

  if (isLoading && (!data || data.length === 0)) {
    return (
      <DataSurface className={cn('h-[364px]', className)}>
        {header}
        <div className="px-4 py-4" role="status" aria-label="Loading timeline data">
          <div className="relative h-[282px] w-full overflow-hidden">
            <div className="absolute right-0 top-0 flex items-center gap-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-20" />
            </div>
            <div className="absolute inset-x-10 bottom-8 top-8 flex flex-col justify-between">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-px w-full rounded-none bg-border/50" />
              ))}
            </div>
            <Skeleton className="absolute bottom-0 left-10 h-3 w-16" />
            <Skeleton className="absolute bottom-0 left-1/3 h-3 w-16" />
            <Skeleton className="absolute bottom-0 right-10 h-3 w-16" />
          </div>
        </div>
      </DataSurface>
    );
  }

  if (!data || data.length === 0) {
    return (
      <DataSurface className={cn('h-[364px]', className)}>
        {header}
        <div className="px-4 py-4">
          <div className="flex h-[282px] items-center justify-center text-sm text-subtle">
            No timeline data for this range.
          </div>
        </div>
      </DataSurface>
    );
  }

  return (
    <DataSurface className={cn('h-[364px]', className)}>
      {header}
      <div className="px-4 py-4">
        <div className="mb-2 flex justify-end gap-5 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-[2px]" style={{ backgroundColor: TOTAL_LINE_COLOR }} />
            <span>All sessions</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-[2px]" style={{ backgroundColor: QUALIFIED_LINE_COLOR }} />
            <span>Qualified</span>
          </div>
        </div>
        <ChartContainer config={chartConfig} className="aspect-auto h-[257px] w-full">
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="hsl(var(--border) / 0.55)" />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.toLocaleString()}
              domain={[0, 'auto']}
              width={28}
              className="text-subtle"
              tick={{ fontSize: 12 }}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={40}
              className="text-subtle"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (isNaN(date.getTime())) return '--';
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                } catch {
                  return '--';
                }
              }}
            />
            <ChartTooltip
              cursor={{ strokeWidth: 1, className: 'stroke-[hsl(var(--border))]' }}
              content={
                <ChartTooltipContent
                  className="w-[180px] card-bg border border-default shadow-lg"
                  labelFormatter={(value) => {
                    try {
                      const date = new Date(value);
                      if (isNaN(date.getTime())) return 'Invalid Date';
                      return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      });
                    } catch {
                      return 'Invalid Date';
                    }
                  }}
                  indicator="dot"
                />
              }
            />
            <Line
              dataKey="total"
              type="monotone"
              stroke="var(--color-total)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2 }}
            />
            <Line
              dataKey="qualified"
              type="monotone"
              stroke="var(--color-qualified)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2 }}
            />
          </LineChart>
        </ChartContainer>
      </div>
    </DataSurface>
  );
};
